import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  inject,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { DrawerModule } from 'primeng/drawer';

import { AuthService } from '@auth/services/auth.service';
import { Utilizador } from '@core/models/models';
import { FeedbackService } from '@core/services/feedback.service';

function passwordsMatchValidator(
  group: AbstractControl,
): ValidationErrors | null {
  const novaPassword = group.get('novaPassword');
  const confirmar = group.get('confirmarNovaPassword');
  if (novaPassword && confirmar && novaPassword.value !== confirmar.value) {
    confirmar.setErrors({ ...confirmar.errors, passwordMismatch: true });
  } else if (confirmar?.errors?.['passwordMismatch']) {
    const errors = { ...confirmar.errors };
    delete errors['passwordMismatch'];
    confirmar.setErrors(Object.keys(errors).length ? errors : null);
  }
  return null;
}

@Component({
  selector: 'app-resetar-senha-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DrawerModule],
  templateUrl: './resetar-senha-dialog.component.html',
  styleUrl: './resetar-senha-dialog.component.scss',
})
export class ResetarSenhaDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() utilizador: Utilizador | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  submitted = false;
  showPassword = false;
  geradaAgora = false;

  private readonly fb = inject(FormBuilder);
  private readonly authSvc = inject(AuthService);
  private readonly feedback = inject(FeedbackService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.initForm();
  }

  private initForm(): void {
    this.form = this.fb.group(
      {
        novaPassword: ['', [Validators.required, Validators.minLength(8)]],
        confirmarNovaPassword: ['', [Validators.required]],
      },
      { validators: passwordsMatchValidator },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.form.reset();
      this.submitted = false;
      this.showPassword = false;
      this.geradaAgora = false;
    }
  }

  get f() {
    return this.form.controls;
  }

  gerarPassword(): void {
    const maiusculas = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const minusculas = 'abcdefghijkmnpqrstuvwxyz';
    const digitos = '23456789';
    const simbolos = '!@#$%&*?';
    const todos = maiusculas + minusculas + digitos + simbolos;

    const randomChar = (charset: string) => {
      const idx =
        crypto.getRandomValues(new Uint32Array(1))[0] % charset.length;
      return charset[idx];
    };

    const obrigatorios = [
      randomChar(maiusculas),
      randomChar(minusculas),
      randomChar(digitos),
      randomChar(simbolos),
    ];
    const resto = Array.from({ length: 8 }, () => randomChar(todos));
    const chars = [...obrigatorios, ...resto];

    // Fisher–Yates
    for (let i = chars.length - 1; i > 0; i--) {
      const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }

    const senha = chars.join('');

    this.form.patchValue({ novaPassword: senha, confirmarNovaPassword: senha });
    this.showPassword = true;
    this.geradaAgora = true;
  }

  copiarPassword(): void {
    const senha = this.form.get('novaPassword')?.value;
    if (!senha) return;
    navigator.clipboard.writeText(senha).then(() => {
      this.feedback.showSuccess('Palavra-passe copiada para a área de transferência.', {
        summary: 'Copiada',
        life: 2500,
      });
    });
  }

  closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.form.reset();
    this.submitted = false;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.form.invalid || !this.utilizador) {
      this.form.markAllAsTouched();
      this.feedback.showWarning('Preencha a nova palavra-passe com pelo menos 8 caracteres e confirme-a.', {
        summary: 'Formulário Incompleto',
        life: 4000,
      });
      return;
    }

    this.loading = true;
    const { novaPassword } = this.form.value;

    this.authSvc
      .resetarSenha({
        userName: this.utilizador.userName,
        email: this.utilizador.email,
        novaPassword,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (res) => {
          const detail = this.feedback.extractSuccessMessage(
            res,
            `A palavra-passe de ${this.utilizador?.nome} foi atualizada com sucesso.`,
          );
          this.feedback.showSuccess(detail, {
            summary: 'Palavra-passe Atualizada',
            life: 4000,
          });
          this.saved.emit();
          setTimeout(() => this.closeDialog(), 800);
        },
        error: () => {
          // O interceptor global já apresenta a mensagem de erro do backend com precisão
        },
      });
  }
}
